import React, { useState } from 'react';
import {
  Box,
  Link,
  SvgIcon,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import { BrandLogo } from '../../../components';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { useGoogleOAuth } from '../../../hooks';
import { LpSignInConsent } from '../../termsOfUse/LpSignInConsent';
import { ReactComponent as GoogleLogo } from 'src/assets/google-logo.svg';
import { Link as RouterLink } from 'react-router-dom';

interface IProps {
  onBack: () => void;
  onGoogleSignIn?: () => void;
  isLoading?: boolean;
}

export const GoogleConsentScreen: React.FC<IProps> = ({
  onBack,
  onGoogleSignIn,
  isLoading: externalLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    isReady,
    signIn,
    isLoading: oauthLoading,
  } = useGoogleOAuth({
    onError: (error: any) => {
      setError(
        typeof error === 'string'
          ? error
          : 'An error occurred during Google sign-in',
      );
    },
  });

  const isButtonLoading = externalLoading || oauthLoading;

  const handleConsentChange = (checked: boolean) => {
    setIsConsentChecked(checked);
    if (checked && error) {
      setError(null);
    }
  };

  const handleGoogleSignIn = () => {
    if (!isConsentChecked) {
      setError('Please agree to the terms to continue');
      return;
    }

    if (!isReady) {
      setError('Google services are not ready. Please try again in a moment.');
      return;
    }

    setError(null);
    onGoogleSignIn?.();
    signIn();
  };

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.background.backgroundPrimary,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '2rem 1.75rem 0',
        [theme.breakpoints.down('md')]: {
          padding: '4.5rem 5.5rem 0',
        },
        [theme.breakpoints.down('sm')]: {
          padding: '3.25rem 0 0',
        },
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      })}
    >
      <Box
        data-testid="consent-brand-logo"
        sx={{
          alignSelf: 'flex-start',
          mb: 4,
          px: { xs: 2, sm: 0 },
        }}
      >
        <BrandLogo width={94} />
      </Box>

      <Box
        data-testid="consent-layout"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '59.5rem',
          borderRadius: '48px 48px 0 0',
          background: 'white',
          alignSelf: 'center',
          px: 12,
          pb: 0,
          [theme.breakpoints.down('md')]: {
            px: 8,
            width: '90%',
          },
          [theme.breakpoints.down('sm')]: {
            px: 4,
            width: '100%',
            borderRadius: '24px 24px 0 0',
          },
        })}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          my={6}
          sx={{
            px: { xs: 2, sm: 0 },
          }}
        >
          <Box display="flex" alignItems="center" onClick={onBack}>
            <SvgIcon
              inheritViewBox
              component={ArrowBackRoundedIcon}
              sx={(theme) => ({
                color: 'brand.accent',
                cursor: 'pointer',
                transition: '500ms',
                ':hover': {
                  color: 'text.primary',
                },
                mr: 2,
                [theme.breakpoints.down('md')]: {
                  ml: -1,
                  mr: 1,
                  mt: 0.25,
                },
              })}
              role="button"
            />
            <Link
              color="text.secondary"
              sx={(theme) => ({
                textDecorationThickness: '0.5px',
                textUnderlinePosition: 'from-font',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                },
              })}
            >
              Back
            </Link>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <Link
              to="/terms-of-use"
              component={RouterLink}
              target="_blank"
              color="text.secondary"
              sx={(theme) => ({
                textDecorationThickness: '0.5px',
                textUnderlinePosition: 'from-font',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                },
              })}
            >
              Terms of use
            </Link>
            <Link
              href="https://agileengine.com/privacy-policy"
              target="_blank"
              color="text.secondary"
              sx={(theme) => ({
                textDecorationThickness: '0.5px',
                textUnderlinePosition: 'from-font',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                },
              })}
            >
              Privacy policy
            </Link>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 0 },
            mb: 6,
          }}
        >
          <LpSignInConsent />
        </Box>

        <Box
          sx={(theme) => ({
            position: 'sticky',
            bottom: 0,
            background: isConsentChecked
              ? '#EBF5FF'
              : theme.palette.highlight.accent,
            padding: '24px 96px 32px',
            borderRadius: '4px',
            marginLeft: -12,
            marginRight: -12,
            paddingLeft: '96px',
            paddingRight: '96px',
            [theme.breakpoints.down('md')]: {
              padding: '16px 32px 24px',
              marginLeft: -8,
              marginRight: -8,
              paddingLeft: '32px',
              paddingRight: '32px',
            },
            [theme.breakpoints.down('sm')]: {
              padding: '8px 8px 24px',
              marginLeft: -4,
              marginRight: -4,
              paddingLeft: '16px',
              paddingRight: '16px',
            },
          })}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
            justifyContent="space-between"
            spacing={2}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConsentChecked}
                  onChange={(e) => handleConsentChange(e.target.checked)}
                  name="googleConsent"
                />
              }
              label="I agree to the personal data processing terms"
            />

            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <CustomButton
                fullWidth={isMobile}
                onClick={handleGoogleSignIn}
                label="Sign in with Google"
                testid="sign-in-with-google-button"
                color="white"
                isDisabled={!isConsentChecked || !isReady}
                isLoading={isButtonLoading}
                startIcon={
                  <SvgIcon
                    inheritViewBox
                    component={GoogleLogo}
                    sx={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                }
                style={{
                  width: isMobile ? '100%' : '228px',
                  whiteSpace: 'nowrap',
                  minWidth: '0',
                }}
              />

              {!!error && (
                <Typography
                  variant="caption"
                  color="error"
                  textAlign="center"
                  sx={{ mt: -1 }}
                >
                  {error}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
